import React, { useRef, useMemo, useEffect, forwardRef } from "react";
import CropIcon from "@material-ui/icons/Crop";
import FolderOpenIcon from "@material-ui/icons/FolderOpen";
import { IconButton } from "@material-ui/core";
import PictureDisplay from "../../../global/PictureDisplay";

export default function (props) {
  const imageRefs = useRef({});
  const imageColumnRefs = useRef({});

  const { settings, eventId, contentLayoutModuleId, contentModuleId, environment, selectedModule, w: moduleWidth, isSmartphone } = props;
  const { columns, horizontalGroup, horizontalColumns: _horizontalColumns, lockedImageSize, imageMaxWidth } = settings;

  const viewWidth = useMemo(() => `calc((100vw - 320px) * (${moduleWidth} / (12*${columns?.length})))`, [moduleWidth]);

  const isHidden = useMemo(() => columns?.filter(({ showImage }) => !showImage)?.length === columns?.length && columns.filter(({ showText }) => !showText)?.length === columns?.length, []);

  if (isHidden) return null;

  const getMinHeight = (imageRefs, columnRef) => {
    return Math.max(
      ...Object.values(imageRefs)?.map((element) => {
        const { offsetHeight, id } = element;

        const columnIndex = id?.split("_")[2];
        const computedStyle = window.getComputedStyle(element);
        const paddingTop = parseFloat(computedStyle.paddingTop);
        const paddingBottom = parseFloat(computedStyle.paddingBottom);
        let baseHeight = offsetHeight + paddingTop + paddingBottom;

        if (!columns[columnIndex]?.horizontalImages) {
          const imageLength = columns[columnIndex]?.images?.length;
          baseHeight = imageLength * baseHeight ?? 0;
        }

        if (columnRef[columnIndex]) {
          const computedColumnStyle = window.getComputedStyle(columnRef[columnIndex]);
          baseHeight += parseFloat(computedColumnStyle.paddingTop) + parseFloat(computedColumnStyle.paddingBottom);
        }

        return baseHeight ?? 0;
      })
    );
  };

  return (
    <div className="cms-content-group" style={{ display: "flex", width: "100%", flexDirection: horizontalGroup && !isSmartphone ? "row" : "column" }}>
      {columns?.map(
        (
          {
            images,
            text,
            showImage = true,
            justifyContent = "center",
            alignItems = "center",
            showText,
            textColWidth,
            overriddenAspectRatio,
            overrideAspectRatio,
            horizontalColumns,
            columnWidth,
            reversed,
            horizontalImages,
            imageColWidth,
          },
          index
        ) => (
          <div
            className="group-column"
            key={`image-group_${index}_${contentLayoutModuleId}`}
            style={{
              display: "flex",
              width: horizontalGroup && !isSmartphone ? columnWidth ?? `calc(100%  / ${columns?.length})` : isSmartphone ? "100%" : columnWidth ?? "auto",
              flexDirection: (horizontalColumns || _horizontalColumns) && !isSmartphone ? (reversed ? "row-reverse" : "row") : reversed ? "column-reverse" : "column",
            }}
          >
            {showImage && (
              <div
                ref={(ref) => (imageColumnRefs.current[index] = ref)}
                className="image-column"
                style={{
                  height: imageRefs.current?.length > 1 ? getMinHeight(imageRefs.current, imageColumnRefs.current) : undefined,
                  width: !showText || isSmartphone ? "100%" : imageColWidth ?? "100%",
                  display: "flex",
                  alignItems,
                  justifyContent,
                  flexDirection: horizontalImages ? "row" : "column",
                }}
              >
                {images?.map((image, imageIndex) => (
                  <Image
                    key={imageIndex}
                    image={image}
                    columns={images?.length}
                    columnIndex={index}
                    imageIndex={imageIndex}
                    overriddenAspectRatio={overriddenAspectRatio}
                    overrideAspectRatio={overrideAspectRatio}
                    contentModuleId={contentModuleId}
                    isSelected={selectedModule === contentModuleId}
                    eventId={eventId}
                    lockedImageSize={lockedImageSize}
                    imageMaxWidth={imageMaxWidth}
                    viewWidth={viewWidth}
                    ref={imageRefs}
                  />
                ))}
              </div>
            )}
            {showText && (
              <div className="text-column cms-text-content" style={{ width: !showImage ? "100%" : textColWidth ?? "100%" }}>
                <div dangerouslySetInnerHTML={{ __html: text?.[environment?.lang] ?? "" }} />
              </div>
            )}
          </div>
        )
      )}
    </div>
  );
}

const Image = forwardRef(({ image, columnIndex, imageIndex, lockedImageSize, imageMaxWidth, viewWidth, contentModuleId, overrideAspectRatio, overriddenAspectRatio }, ref) => {
  const imageRef = useRef();

  const imageStyle = {
    maxWidth: "100%",
    maxHeight: "100%",
    display: 'flex',
    height: ref.current?.[columnIndex]?.[imageIndex]?.naturalHeight > ref.current?.[columnIndex]?.[imageIndex]?.naturalWidth ? "100%" : undefined,
    width: ref.current?.[columnIndex]?.[imageIndex]?.naturalHeight > ref.current?.[columnIndex]?.[imageIndex]?.naturalWidth ? undefined : "100%",
    aspectRatio:
      overrideAspectRatio && overriddenAspectRatio && (/\d+ ?\/ ?\d+/.test(overriddenAspectRatio) || overriddenAspectRatio === "1")
        ? overriddenAspectRatio
        : `${ref?.[columnIndex]?.[imageIndex]?.naturalWidth} / ${ref.current?.[columnIndex]?.[imageIndex]?.naturalHeight}`,
  };

  useEffect(() => {
    ref.current[`col_${columnIndex}_image_${imageIndex}`] = imageRef.current;
  }, [columnIndex, imageIndex, ref, imageRef]);

  return (
    <div style={{ position: "relative", width: lockedImageSize ? imageMaxWidth * 4 : undefined, maxHeight: "100%" }}>
      <PictureDisplay id={`${contentModuleId}_col_${columnIndex}`} src={image.src} srcset={image.srcset} viewWidth={viewWidth} style={imageStyle} ref={imageRef} />
    </div>
  );
});
